import {ChangeEvent, FC, useCallback, useState} from "react";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import PublicIcon from "@mui/icons-material/Public";
import apiClient from "../../lib/api.ts";
import Switch from "@mui/material/Switch";
import {useFeedback} from "@memaro/feedback";
import FeedbackDialog from "@memaro/components/FeedbackDialog.tsx";
import {ListItemText} from "../Menu.tsx";
import HelpButton from "../../base/HelpButton.tsx";

export type PublicToggleProps = {
    profileID: string
    isPublic: boolean
    disabled?: boolean
    onChange: (value: boolean) => void
}

const PublicToggle: FC<PublicToggleProps> = ({profileID, isPublic, disabled, onChange}) => {
    const [dialogOpen, setDialogOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const {error} = useFeedback()

    const handleDialogClose = useCallback(() => setDialogOpen(false), [])

    const handleConfirm = useCallback(() => {
        const targetValue = !isPublic
        setLoading(true)
        apiClient.setPublicFlag(profileID, targetValue)
            .then(() => onChange(targetValue))
            .catch(() => error?.("Er is een fout opgetreden bij het aanpassen van de publieke toegankelijkheid"))
            .finally(() => {
                handleDialogClose()
                setLoading(false)
            })
    }, [isPublic, profileID, error, handleDialogClose, onChange])

    const handleToggle = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (isPublic) {
            handleConfirm()
        } else {
            setDialogOpen(true)
        }
    }, [handleConfirm, isPublic])

    return (
        <>
            <ListItem>
                <ListItemIcon>
                    <PublicIcon/>
                </ListItemIcon>
                <ListItemText primary={(
                    <>
                        Publiek toegankelijk
                        <HelpButton help="Hiermee maakt u de volledige herinneringspagina publiek toegankelijk. Dit betekent dat iedereen met de link de pagina kan bekijken, ook als ze geen account hebben bij Overleven"/>
                    </>)
                }/>
                <Switch
                    edge="end"
                    onChange={handleToggle}
                    checked={isPublic}
                    disabled={loading || disabled}
                />
            </ListItem>
            <FeedbackDialog
                id="make-public"
                open={dialogOpen}
                title="Weet u het zeker?"
                contents="Weet u zeker dat u de volledige herinneringspagina publiek toegankelijk wil maken?"
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    )
}

export default PublicToggle
